import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CustomSelect } from '@/components/ui/custom-select';
import { SearchIcon } from '@/components/layout/icons';
import { apiErrorMessage } from '@shared/api/client';
import { listMyServices, setMyServices } from '@shared/api/associates';
import { useAllServices } from '@shared/hooks/use-services';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
} from '@shared/types/api';
import { ServiceCard } from '@/features/client/web/service-card';
import { ServiceDetailPage } from '@/features/client/web/service-detail-page';
import {
  AuthorityCard,
  CategoryCard,
  EmptyState,
  CATEGORY_IMAGES,
} from '@/features/client/web/services-page';
import { cn } from '@/lib/cn';

type TakeBrowseMode = 'AUTHORITY' | 'CATEGORY';

export function ServicesOfferedPage() {
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<'my' | 'take'>('my');

  // -------------------------------------------------------------
  // "My Services" State & Logic (Preserved 100%)
  // -------------------------------------------------------------
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [selectedAuthorityId, setSelectedAuthorityId] = useState<string>('ALL');

  const { data: fullCatalog = [], isLoading: catalogLoading } = useAllServices();

  const queryParams = useMemo(
    () => ({
      q: q.trim() || undefined,
      authorityId: selectedAuthorityId !== 'ALL' ? selectedAuthorityId : undefined,
    }),
    [q, selectedAuthorityId],
  );
  const { data: filteredCatalog = [] } = useAllServices(queryParams);

  const { data: mine = [], isLoading: mineLoading } = useQuery({
    queryKey: ['associate', 'services'],
    queryFn: listMyServices,
    staleTime: 30_000,
  });

  useEffect(() => {
    const activeMineIds = mine.filter((s) => s.isActive !== false).map((s) => s.id);
    setSelected(new Set(activeMineIds));
  }, [mine]);

  const mineSet = useMemo(
    () => new Set(mine.filter((s) => s.isActive !== false).map((s) => s.id)),
    [mine],
  );

  const availableAuthorities = useMemo(() => {
    const map = new Map<string, { id: string | null; name: string; code: string | null }>();
    for (const s of fullCatalog) {
      const key = s.authority ? s.authority.id : 'DIRECT_SERVICES';
      if (!map.has(key)) {
        map.set(key, {
          id: s.authority?.id ?? null,
          name: s.authority ? s.authority.name : 'Direct Services',
          code: s.authority?.code ?? null,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [fullCatalog]);

  const authorityOptions = useMemo(() => {
    const options = [{ value: 'ALL', label: `All Authorities (${availableAuthorities.length})` }];
    for (const auth of availableAuthorities) {
      const val = auth.id === null ? 'DIRECT_SERVICES' : auth.id;
      const label = `${auth.name}${auth.code ? ` (${auth.code})` : ''}`;
      options.push({ value: val, label });
    }
    return options;
  }, [availableAuthorities]);

  const authorityGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string | null;
        name: string;
        code: string | null;
        services: typeof filteredCatalog;
      }
    >();

    for (const s of filteredCatalog) {
      const authKey = s.authority ? s.authority.id : 'DIRECT_SERVICES';
      const authName = s.authority ? s.authority.name : 'Direct Services';
      const authCode = s.authority?.code ?? null;

      const existing = map.get(authKey) ?? {
        id: s.authority?.id ?? null,
        name: authName,
        code: authCode,
        services: [],
      };
      existing.services.push(s);
      map.set(authKey, existing);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredCatalog]);

  const dirty = selected.size !== mineSet.size || [...selected].some((id) => !mineSet.has(id));

  const save = useMutation({
    mutationFn: () => setMyServices([...selected]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['associate', 'services'] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // -------------------------------------------------------------
  // "Take Services" State & Dynamic Grouping (Client Panel UI Style)
  // -------------------------------------------------------------
  const [takeQuery, setTakeQuery] = useState('');
  const [takeBrowseMode, setTakeBrowseMode] = useState<TakeBrowseMode>('AUTHORITY');
  const [takeAuthorityId, setTakeAuthorityId] = useState<string | null>(null);
  const [takeCategory, setTakeCategory] = useState<string | null>(null);
  const [takeServiceDetailId, setTakeServiceDetailId] = useState<string | null>(null);

  // Group services by category dynamically from catalog
  const categoriesData = useMemo(() => {
    const map = new Map<string, ServiceSummary[]>();
    for (const cat of SERVICE_CATEGORIES) {
      map.set(cat, []);
    }
    for (const s of fullCatalog) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }
    return Array.from(map.entries()).map(([catKey, list]) => ({
      key: catKey,
      label: SERVICE_CATEGORY_LABELS[catKey as ServiceCategory] ?? catKey.replace(/_/g, ' '),
      image: CATEGORY_IMAGES[catKey] ?? null,
      services: list,
      count: list.length,
    }));
  }, [fullCatalog]);

  // Group services by authority dynamically from catalog
  const authoritiesData = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string | null;
        name: string;
        code: string | null;
        image: string | null;
        createdAt: string | Date | null;
        services: ServiceSummary[];
      }
    >();

    for (const s of fullCatalog) {
      const authKey = s.authority ? s.authority.id : 'DIRECT_SERVICES';
      const authName = s.authority ? s.authority.name : 'Direct Services';
      const authCode = s.authority?.code ?? null;
      const authImage = s.authority?.images?.[0] ?? null;
      const authCreatedAt = s.authority?.createdAt ?? null;

      const existing = map.get(authKey) ?? {
        id: s.authority?.id ?? null,
        name: authName,
        code: authCode,
        image: authImage,
        createdAt: authCreatedAt,
        services: [],
      };
      if (!existing.image && authImage) {
        existing.image = authImage;
      }
      if (!existing.createdAt && authCreatedAt) {
        existing.createdAt = authCreatedAt;
      }
      existing.services.push(s);
      map.set(authKey, existing);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      if (a.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }, [fullCatalog]);

  // Search results for Take Services mode
  const takeSearchResults = useMemo(() => {
    const term = takeQuery.trim().toLowerCase();
    if (!term) return [];
    return fullCatalog.filter((s) => {
      const nameMatch = s.name.toLowerCase().includes(term);
      const descMatch = (s.description ?? '').toLowerCase().includes(term);
      const catMatch = (SERVICE_CATEGORY_LABELS[s.category] ?? s.category)
        ?.toLowerCase()
        .includes(term);
      const authNameMatch = s.authority?.name.toLowerCase().includes(term);
      const authCodeMatch = s.authority?.code?.toLowerCase().includes(term);
      return nameMatch || descMatch || catMatch || authNameMatch || authCodeMatch;
    });
  }, [fullCatalog, takeQuery]);

  // Services matching selected Category
  const selectedCategoryServices = useMemo(() => {
    if (!takeCategory) return [];
    return fullCatalog.filter((s) => s.category === takeCategory);
  }, [fullCatalog, takeCategory]);

  // Services matching selected Authority
  const selectedAuthorityData = useMemo(() => {
    if (!takeAuthorityId) return null;
    return authoritiesData.find(
      (a) => (a.id === null ? 'DIRECT_SERVICES' : a.id) === takeAuthorityId,
    );
  }, [authoritiesData, takeAuthorityId]);

  const isTakeSearchActive = takeQuery.trim().length > 0;
  const isTakeDrillDownActive =
    !isTakeSearchActive && (takeCategory !== null || takeAuthorityId !== null);

  const handleClearTakeDrilldown = () => {
    setTakeCategory(null);
    setTakeAuthorityId(null);
  };

  // If viewing a single service detail in Take Services mode, render the rich detail view
  if (activeTab === 'take' && takeServiceDetailId) {
    return (
      <ServiceDetailPage
        serviceId={takeServiceDetailId}
        onBack={() => setTakeServiceDetailId(null)}
        homePath="/associate/transactions"
        servicesPath="/associate/services"
      />
    );
  }

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      {/* Breadcrumb Navigation */}

      {/* Header */}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {activeTab === 'my' ? 'Services you offer' : 'Take a Service'}
          </h1>
          <p className="text-sm text-gray-600">
            {activeTab === 'my'
              ? 'Select the services you know and offer. Admin will match you to incoming client requests.'
              : 'Browse and apply for services as a client.'}
          </p>
        </div>
        <div className="text-sm text-gray-600 shrink-0">
          {activeTab === 'my'
            ? `${selected.size} / ${fullCatalog.length} selected`
            : `${fullCatalog.length} available`}
        </div>
      </div>

      {/* Switcher Chips: My Services vs Take Services */}
      <div className="mt-5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setActiveTab('my');
            setTakeServiceDetailId(null);
          }}
          className={cn(
            'px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase transition-all shadow-xs border cursor-pointer',
            activeTab === 'my'
              ? 'bg-brand-700 border-brand-700 text-white font-bold'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          My Services
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab('take');
            setTakeServiceDetailId(null);
          }}
          className={cn(
            'px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase transition-all shadow-xs border cursor-pointer',
            activeTab === 'take'
              ? 'bg-brand-700 border-brand-700 text-white font-bold'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          Take Services
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: MY SERVICES (Checkbox list + Save / Discard)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'my' && (
        <>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full max-w-md">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services..."
                className="pl-9"
              />
            </div>
            <div className="w-full sm:w-64">
              <CustomSelect
                value={selectedAuthorityId}
                onChange={setSelectedAuthorityId}
                options={authorityOptions}
                placeholder="Select Authority"
              />
            </div>
          </div>

          <section className="mt-5">
            {catalogLoading || mineLoading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                Loading…
              </div>
            ) : authorityGroups.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                No services match.
              </div>
            ) : (
              <div className="space-y-6">
                {authorityGroups.map((group) => {
                  const selectedInGroupCount = group.services.filter((s) => selected.has(s.id)).length;
                  return (
                    <div
                      key={group.id ?? 'direct'}
                      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs"
                    >
                      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50/80 px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <h2 className="text-sm font-bold text-gray-900 truncate">{group.name}</h2>
                          {group.code ? (
                            <span className="rounded bg-brand-50 px-2 py-0.5 text-[10px] font-extrabold uppercase text-brand-700 border border-brand-200">
                              {group.code}
                            </span>
                          ) : null}
                        </div>
                        <span className="text-xs font-semibold text-gray-500 shrink-0">
                          {selectedInGroupCount} / {group.services.length} selected
                        </span>
                      </div>

                      <ul className="divide-y divide-gray-100">
                        {group.services.map((s) => {
                          const checked = selected.has(s.id);
                          return (
                            <li
                              key={s.id}
                              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors"
                            >
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <input
                                  id={`svc-${s.id}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggle(s.id)}
                                  className="mt-1 h-4 w-4 accent-brand-700 shrink-0 cursor-pointer"
                                />
                                <label
                                  htmlFor={`svc-${s.id}`}
                                  className="min-w-0 flex-1 cursor-pointer"
                                >
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                      className={cn(
                                        'text-sm font-medium',
                                        checked ? 'text-brand-800 font-semibold' : 'text-gray-900',
                                      )}
                                    >
                                      {s.name}
                                    </span>
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-600">
                                      {s.category}
                                    </span>
                                  </div>
                                  {s.description ? (
                                    <div className="mt-0.5 text-xs text-gray-600 truncate">
                                      {s.description}
                                    </div>
                                  ) : null}
                                </label>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {save.isError ? (
            <p className="mt-4 text-sm text-red-600">{apiErrorMessage(save.error)}</p>
          ) : null}
          {save.isSuccess && !dirty ? <p className="mt-4 text-sm text-brand-700">Saved.</p> : null}

          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => setSelected(new Set(mine.map((s) => s.id)))}
              disabled={!dirty || save.isPending}
            >
              Discard
            </Button>
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: TAKE SERVICES (Client Panel Style UI)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'take' && (
        <div className="mt-5 space-y-6">
          {/* Top Search Bar */}
          <div className="relative w-full max-w-md">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={takeQuery}
              onChange={(e) => setTakeQuery(e.target.value)}
              placeholder="Search service, category or authority..."
              className="pl-9 text-base md:text-sm"
            />
            {takeQuery && (
              <button
                type="button"
                onClick={() => setTakeQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {/* Level 2: Search Results View */}
          {isTakeSearchActive ? (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-bold text-gray-900">
                  Search Results ({takeSearchResults.length})
                </h2>
                <button
                  type="button"
                  onClick={() => setTakeQuery('')}
                  className="text-xs font-semibold text-brand-700 hover:underline cursor-pointer"
                >
                  Clear search
                </button>
              </div>
              {takeSearchResults.length === 0 ? (
                <EmptyState query={takeQuery} />
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {takeSearchResults.map((s) => (
                    <ServiceCard
                      key={s.id}
                      service={s}
                      onClick={() => setTakeServiceDetailId(s.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : isTakeDrillDownActive ? (
            /* Level 2: Category or Authority Drill-down View */
            <div>
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                <button
                  type="button"
                  onClick={handleClearTakeDrilldown}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-1.5 text-xs font-bold text-gray-700 shadow-sm transition-all hover:bg-brand-50 hover:text-brand-800 hover:border-brand-300 active:scale-95 cursor-pointer"
                >
                  <svg
                    className="h-4 w-4 text-brand-700"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                    />
                  </svg>
                  <span>Back to {takeCategory ? 'Categories' : 'Authorities'}</span>
                </button>

                <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-extrabold text-brand-800 border border-brand-200">
                  {takeCategory
                    ? `${selectedCategoryServices.length} Services`
                    : `${selectedAuthorityData?.services.length ?? 0} Services`}
                </span>
              </div>

              {(takeCategory ? selectedCategoryServices : (selectedAuthorityData?.services ?? []))
                .length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 p-8">
                  <h3 className="text-base font-bold text-gray-900">No services available</h3>
                  <p className="mt-1 max-w-sm text-sm text-gray-500">
                    There are currently no services listed under this{' '}
                    {takeCategory ? 'category' : 'authority'}.
                  </p>
                  <button
                    type="button"
                    onClick={handleClearTakeDrilldown}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-800 transition-colors cursor-pointer"
                  >
                    Browse all {takeCategory ? 'categories' : 'authorities'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(takeCategory
                    ? selectedCategoryServices
                    : (selectedAuthorityData?.services ?? [])
                  ).map((s) => (
                    <ServiceCard
                      key={s.id}
                      service={s}
                      onClick={() => setTakeServiceDetailId(s.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Level 1: Cards Selection Overview View (Authority vs Category) */
            <div>
              {/* Mode Switcher Tabs */}
              <div className="mb-5 flex items-center justify-between border-b border-gray-200 pb-3">
                <div className="flex items-center gap-1.5 rounded-xl bg-gray-100 p-1">
                  <button
                    type="button"
                    onClick={() => setTakeBrowseMode('AUTHORITY')}
                    className={cn(
                      'rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all duration-150 cursor-pointer',
                      takeBrowseMode === 'AUTHORITY'
                        ? 'bg-brand-700 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900',
                    )}
                  >
                    By Authority ({authoritiesData.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setTakeBrowseMode('CATEGORY')}
                    className={cn(
                      'rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all duration-150 cursor-pointer',
                      takeBrowseMode === 'CATEGORY'
                        ? 'bg-brand-700 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900',
                    )}
                  >
                    By Category
                  </button>
                </div>

                <span className="hidden text-xs font-semibold text-gray-500 sm:inline-block">
                  Tap card to view services
                </span>
              </div>

              {/* Authority Grid */}
              <div
                className={cn(
                  'grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-2',
                  takeBrowseMode !== 'AUTHORITY' && 'hidden',
                )}
              >
                {authoritiesData.map((authItem) => {
                  const authKey = authItem.id === null ? 'DIRECT_SERVICES' : authItem.id;
                  return (
                    <AuthorityCard
                      key={authKey}
                      name={authItem.name}
                      code={authItem.code}
                      image={authItem.image}
                      count={authItem.services.length}
                      onClick={() => setTakeAuthorityId(authKey)}
                    />
                  );
                })}
              </div>

              {/* Category Grid */}
              <div
                className={cn(
                  'grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-2',
                  takeBrowseMode !== 'CATEGORY' && 'hidden',
                )}
              >
                {categoriesData.map((catItem) => (
                  <CategoryCard
                    key={catItem.key}
                    label={catItem.label}
                    image={catItem.image}
                    count={catItem.count}
                    onClick={() => setTakeCategory(catItem.key)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
